import React from "react";
import { Box, SimpleGrid, useColorMode } from "@chakra-ui/react";
import StatCard from "../common/StatCard";

// Each entry: label + the stats key + whether the value should be currency-
// formatted and color-coded by sign.
const STAT_DEFS = [
  { label: "Average PNL", key: "avg_pnl", currency: true, colored: true },
  { label: "Wins", key: "num_wins" },
  { label: "Losses", key: "num_losses" },
  { label: "Largest Win", key: "largest_win", currency: true, colored: true },
  { label: "Largest Loss", key: "largest_loss", currency: true, colored: true },
  { label: "Avg Win", key: "avg_win", currency: true, colored: true },
  { label: "Avg Loss", key: "avg_loss", currency: true, colored: true },
  { label: "Day Trades", key: "num_day" },
  { label: "Swing Trades", key: "num_swing" },
  { label: "Options", key: "num_options" },
  { label: "Shares", key: "num_shares" },
  { label: "Long", key: "num_long_shares" },
  { label: "Short", key: "num_short_shares" },
  { label: "Calls", key: "num_call_options" },
  { label: "Puts", key: "num_put_options" },
  { label: "Avg Shares/Trade", key: "avg_shares_per_trade" },
  { label: "Avg Contracts/Trade", key: "avg_options_per_trade" },
];

const DetailedStatsGrid = ({
  stats,
  preferences,
  formatter,
  pnlValue,
  colorChange,
  // TRADE-959: new headline metrics from the Insights epic
  planAdherence,
  riskReward,
  accountValueMetrics,
  journalStreak,
}) => {
  const { colorMode } = useColorMode();
  const cardBg = colorMode === "light" ? "white" : "gray.700";

  const planAdherencePct = planAdherence?.followed?.win_pct;
  const avgRR = riskReward?.avg_rr;
  const maxDrawdown = accountValueMetrics?.max_drawdown_pct;
  const currentStreak = journalStreak?.current_streak;

  const insightTiles = [
    {
      key: "plan_adherence",
      label: "Plan Adherence %",
      value: planAdherencePct != null ? `${planAdherencePct}%` : "—",
    },
    {
      key: "avg_rr",
      label: "Avg R:R",
      value: avgRR != null ? Number(avgRR).toFixed(2) : "—",
    },
    {
      key: "max_drawdown",
      label: "Max Drawdown",
      value: maxDrawdown != null ? `${maxDrawdown}%` : "—",
      color: maxDrawdown != null && maxDrawdown > 0 ? "red.400" : undefined,
    },
    {
      key: "journal_streak",
      label: "Journal Streak",
      value: currentStreak != null ? `${currentStreak} day${currentStreak === 1 ? "" : "s"}` : "—",
    },
  ];

  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
      {insightTiles.map(({ key, label, value, color }) => (
        <Box key={key} bg={cardBg} rounded="md" px={4} py={3}>
          <StatCard label={label} value={value} fontSize="md" color={color} />
        </Box>
      ))}
      {STAT_DEFS.map(({ label, key, currency, colored }) => {
        const raw = stats[key];
        const display = currency
          ? pnlValue(formatter(preferences.preferred_currency).format(raw))
          : raw;
        const color = colored ? colorChange(raw) : undefined;
        return (
          <Box key={key} bg={cardBg} rounded="md" px={4} py={3}>
            <StatCard
              label={label}
              value={raw}
              displayValue={display}
              fontSize="md"
              color={color}
            />
          </Box>
        );
      })}
    </SimpleGrid>
  );
};

export default DetailedStatsGrid;
